import type { Diagnosis } from '../../../../types/api';

// ─── Shared Types ─────────────────────────────────────────────

export interface DiagnosisEntry {
  id?: number;
  diagnosis_id: number;
  diagnosis_type: string;
  observations: string;
  diagnosis?: Diagnosis;
}

export interface PrescriptionEntry {
  id?: number;
  medication_id: number | null;
  medication_name: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions: string;
}

export interface LabOrderEntry {
  id?: number;
  test_id: number | null;
  test_name: string;
  panel_id: number | null;
  urgency: 'routine' | 'urgent' | 'stat';
  instructions: string;
}

export interface ImagingOrderEntry {
  id?: number;
  imaging_study_id: number;
  imaging_study_name: string;
  clinical_indication: string;
  priority: 'routine' | 'urgent' | 'stat';
}

export interface VitalSignsEntry {
  heart_rate: string;
  respiratory_rate: string;
  systolic_pressure: string;
  diastolic_pressure: string;
  temperature: string;
  weight: string;
  height: string;
  oxygen_saturation: string;
}

export const emptyVitalSigns: VitalSignsEntry = {
  heart_rate: '',
  respiratory_rate: '',
  systolic_pressure: '',
  diastolic_pressure: '',
  temperature: '',
  weight: '',
  height: '',
  oxygen_saturation: '',
};

// ─── Pediatric ────────────────────────────────────────────────

export interface PediatricData {
  perinatal: {
    gestational_age_weeks: number | null;
    delivery_type: string;
    birth_weight_g: number | null;
    birth_height_cm: number | null;
    birth_head_cm: number | null;
    apgar_1min: number | null;
    apgar_5min: number | null;
    perinatal_complications: string;
  };
  feeding: {
    type: string;
    ablactation_start_month: number | null;
    formula_name: string;
    food_allergies: string;
  };
  growth: {
    weight_kg: number | null;
    height_cm: number | null;
    head_cm: number | null;
    measurement_method: string;
    weight_percentile: number | null;
    height_percentile: number | null;
    head_percentile: number | null;
    bmi: number | null;
    bmi_percentile: number | null;
  };
  milestones: Array<{
    area: string;
    milestone: string;
    achieved: boolean;
  }>;
  milestones_observations: string;
  vaccines: Array<{
    vaccine: string;
    dose: string;
    applied: boolean;
    date: string | null;
  }>;
  vaccination_status: string;
  vaccination_observations: string;
}

export const emptyPediatricData: PediatricData = {
  perinatal: {
    gestational_age_weeks: null,
    delivery_type: '',
    birth_weight_g: null,
    birth_height_cm: null,
    birth_head_cm: null,
    apgar_1min: null,
    apgar_5min: null,
    perinatal_complications: '',
  },
  feeding: {
    type: '',
    ablactation_start_month: null,
    formula_name: '',
    food_allergies: '',
  },
  growth: {
    weight_kg: null,
    height_cm: null,
    head_cm: null,
    measurement_method: '',
    weight_percentile: null,
    height_percentile: null,
    head_percentile: null,
    bmi: null,
    bmi_percentile: null,
  },
  milestones: [],
  milestones_observations: '',
  vaccines: [],
  vaccination_status: '',
  vaccination_observations: '',
};

// ─── Internal Medicine ────────────────────────────────────────

export interface InternalMedicineData {
  diabetes: {
    type: string;
    diagnosis_years: number | null;
    hba1c: number | null;
    hba1c_date: string | null;
    fasting_glucose: number | null;
    foot_exam_done: boolean;
    foot_exam_findings: string;
    fundoscopy_done: boolean;
    fundoscopy_findings: string;
    nephropathy_screening_done: boolean;
    albuminuria: number | null;
    current_treatment: string[];
    observations: string;
  };
  hypertension: {
    stage: string;
    diagnosis_years: number | null;
    avg_sbp: number | null;
    avg_dbp: number | null;
    calculated_stage: string | null;
    stage_overridden: boolean;
    target_organ_damage: Array<{
      organ: string;
      present: boolean;
      details: string;
    }>;
    current_treatment: string[];
    observations: string;
  };
  dyslipidemia: {
    ldl: number | null;
    hdl: number | null;
    triglycerides: number | null;
    total_cholesterol: number | null;
    lipid_profile_date: string | null;
    treatment: string;
    observations: string;
  };
  cv_risk: {
    risk_table: string;
    risk_factors: string[];
    calculated_risk_percent: number | null;
    calculated_risk_category: string | null;
    interpretation: string;
    data_complete: boolean;
    missing_factors: string[];
  };
  metabolic_syndrome: {
    waist_circumference_cm: number | null;
    waist_high: boolean;
    triglycerides_high: boolean;
    hdl_low: boolean;
    bp_high: boolean;
    fasting_glucose_high: boolean;
    criteria_positive_count: number;
    diagnosis: boolean;
    observations: string;
  };
  treatment_plan: {
    hba1c_target: string;
    sbp_target: number | null;
    dbp_target: number | null;
    ldl_target: string;
    next_appointment: string | null;
    next_labs: string | null;
    plan_notes: string;
  };
}

export const emptyInternalMedicineData: InternalMedicineData = {
  diabetes: {
    type: '',
    diagnosis_years: null,
    hba1c: null,
    hba1c_date: null,
    fasting_glucose: null,
    foot_exam_done: false,
    foot_exam_findings: '',
    fundoscopy_done: false,
    fundoscopy_findings: '',
    nephropathy_screening_done: false,
    albuminuria: null,
    current_treatment: [],
    observations: '',
  },
  hypertension: {
    stage: '',
    diagnosis_years: null,
    avg_sbp: null,
    avg_dbp: null,
    calculated_stage: null,
    stage_overridden: false,
    target_organ_damage: [],
    current_treatment: [],
    observations: '',
  },
  dyslipidemia: {
    ldl: null,
    hdl: null,
    triglycerides: null,
    total_cholesterol: null,
    lipid_profile_date: null,
    treatment: '',
    observations: '',
  },
  cv_risk: {
    risk_table: '',
    risk_factors: [],
    calculated_risk_percent: null,
    calculated_risk_category: null,
    interpretation: '',
    data_complete: false,
    missing_factors: [],
  },
  metabolic_syndrome: {
    waist_circumference_cm: null,
    waist_high: false,
    triglycerides_high: false,
    hdl_low: false,
    bp_high: false,
    fasting_glucose_high: false,
    criteria_positive_count: 0,
    diagnosis: false,
    observations: '',
  },
  treatment_plan: {
    hba1c_target: '',
    sbp_target: null,
    dbp_target: null,
    ldl_target: '',
    next_appointment: null,
    next_labs: null,
    plan_notes: '',
  },
};

// ─── Traumatologia ────────────────────────────────────────────

export interface TraumatologiaData {
  fracture: {
    type: string;
    anatomic_location: string;
    side: string;
    bone_segment: string;
    open_closed: string;
    gustilo_classification: string;
    displacement: string;
    articular_extension: boolean;
  };
  injury_mechanism: {
    mechanism: string;
    high_energy: boolean;
    time_since_injury_hours: number | null;
    work_related: boolean;
    circumstances: string;
  };
  neurovascular_exam: {
    motor_function: string;
    sensory_function: string;
    distal_pulse: string;
    capillary_fill: string;
    compartment_syndrome_suspected: boolean;
  };
  physical_exam_trauma: {
    edema: string;
    ecchymosis: string;
    deformity: string;
    range_of_motion_active: string;
    range_of_motion_passive: string;
    stability: string;
    weight_bearing: string;
  };
  imaging: {
    xray_views: string;
    xray_findings: string;
    mri_performed: boolean;
    mri_findings: string;
    ct_performed: boolean;
    ct_findings: string;
  };
  treatment: {
    type: string;
    conservative_plan: string;
    immobilization_type: string;
    immobilization_duration_weeks: number | null;
    surgical_indication: string;
    surgical_plan: string;
    weight_bearing_restriction: string;
    antibiotic_prophylaxis: boolean;
    tetanus_prophylaxis: boolean;
    next_appointment: string | null;
    observations: string;
  };
  complications: {
    neurovascular_deficit: boolean;
    infection: boolean;
    delayed_union: boolean;
    non_union: boolean;
    malunion: boolean;
    compartment_syndrome: boolean;
    thromboembolism_prophylaxis: boolean;
    other: string;
  };
}

export const emptyTraumatologiaData: TraumatologiaData = {
  fracture: { type: '', anatomic_location: '', side: '', bone_segment: '', open_closed: '', gustilo_classification: '', displacement: '', articular_extension: false },
  injury_mechanism: { mechanism: '', high_energy: false, time_since_injury_hours: null, work_related: false, circumstances: '' },
  neurovascular_exam: { motor_function: '', sensory_function: '', distal_pulse: '', capillary_fill: '', compartment_syndrome_suspected: false },
  physical_exam_trauma: { edema: '', ecchymosis: '', deformity: '', range_of_motion_active: '', range_of_motion_passive: '', stability: '', weight_bearing: '' },
  imaging: { xray_views: '', xray_findings: '', mri_performed: false, mri_findings: '', ct_performed: false, ct_findings: '' },
  treatment: { type: '', conservative_plan: '', immobilization_type: '', immobilization_duration_weeks: null, surgical_indication: '', surgical_plan: '', weight_bearing_restriction: '', antibiotic_prophylaxis: false, tetanus_prophylaxis: false, next_appointment: null, observations: '' },
  complications: { neurovascular_deficit: false, infection: false, delayed_union: false, non_union: false, malunion: false, compartment_syndrome: false, thromboembolism_prophylaxis: false, other: '' },
};

// ─── Dermatologia ─────────────────────────────────────────────

export interface DermatologiaData {
  lesion: {
    type: string;
    size_mm: number | null;
    color: string;
    border: string;
    surface: string;
    distribution: string;
    anatomic_location: string;
    number_of_lesions: string;
    symptom: string;
  };
  history: {
    onset: string;
    evolution: string;
    previous_episodes: boolean;
    previous_diagnosis: string;
    previous_treatment: string;
    trigger_factors: string;
    allergies: string;
    family_history: string;
  };
  dermoscopy: {
    performed: boolean;
    findings: string;
    pattern: string;
    ABCD_rule_score: number | null;
    melanoma_suspected: boolean;
    biopsy_recommended: boolean;
  };
  treatment_derma: {
    topical: string;
    systemic: string;
    phototherapy: boolean;
    cryotherapy: boolean;
    laser: boolean;
    surgical_excision: boolean;
    follow_up_weeks: number | null;
    observations: string;
  };
}

export const emptyDermatologiaData: DermatologiaData = {
  lesion: { type: '', size_mm: null, color: '', border: '', surface: '', distribution: '', anatomic_location: '', number_of_lesions: '', symptom: '' },
  history: { onset: '', evolution: '', previous_episodes: false, previous_diagnosis: '', previous_treatment: '', trigger_factors: '', allergies: '', family_history: '' },
  dermoscopy: { performed: false, findings: '', pattern: '', ABCD_rule_score: null, melanoma_suspected: false, biopsy_recommended: false },
  treatment_derma: { topical: '', systemic: '', phototherapy: false, cryotherapy: false, laser: false, surgical_excision: false, follow_up_weeks: null, observations: '' },
};

// ─── Oftalmologia ─────────────────────────────────────────────

export interface OftalmologiaData {
  visual_acuity: {
    sc_od: string;
    sc_oi: string;
    cc_od: string;
    cc_oi: string;
    near_vision: string;
    pinhole_improves: string;
  };
  refraction: {
    sphere_od: string;
    sphere_oi: string;
    cylinder_od: string;
    cylinder_oi: string;
    axis_od: string;
    axis_oi: string;
    add_od: string;
    add_oi: string;
  };
  anterior_segment: {
    conjunctiva: string;
    cornea: string;
    anterior_chamber: string;
    iris: string;
    pupil: string;
    lens: string;
  };
  posterior_segment: {
    vitreous: string;
    optic_disc: string;
    macula: string;
    retina: string;
    vessels: string;
  };
  intraocular_pressure: {
    od: number | null;
    oi: number | null;
    method: string;
    time: string;
  };
  diagnosis_oft: {
    primary: string;
    od_oi: string;
    icd10_code: string;
    type: string;
  };
  treatment_oft: {
    eye_drops: string;
    eye_drops_frequency: string;
    oral_medication: string;
    surgery_indicated: boolean;
    surgery_type: string;
    glasses_prescription: boolean;
    follow_up_days: number | null;
    observations: string;
  };
}

export const emptyOftalmologiaData: OftalmologiaData = {
  visual_acuity: { sc_od: '', sc_oi: '', cc_od: '', cc_oi: '', near_vision: '', pinhole_improves: '' },
  refraction: { sphere_od: '', sphere_oi: '', cylinder_od: '', cylinder_oi: '', axis_od: '', axis_oi: '', add_od: '', add_oi: '' },
  anterior_segment: { conjunctiva: '', cornea: '', anterior_chamber: '', iris: '', pupil: '', lens: '' },
  posterior_segment: { vitreous: '', optic_disc: '', macula: '', retina: '', vessels: '' },
  intraocular_pressure: { od: null, oi: null, method: '', time: '' },
  diagnosis_oft: { primary: '', od_oi: '', icd10_code: '', type: '' },
  treatment_oft: { eye_drops: '', eye_drops_frequency: '', oral_medication: '', surgery_indicated: false, surgery_type: '', glasses_prescription: false, follow_up_days: null, observations: '' },
};
